import { useMutation } from "@tanstack/react-query";
import { useContext } from "react";
import { AlertBannerContext } from "@kivunova/kivufrontendcommon";
import { fetchGraphQL } from "../../Graphql/utils";
import { useDispatch } from "react-redux";
import {
  ALERT_BANNER_CODE,
  ALERT_BANNER_CODE_SEVERITY
} from "@kivunova/kivufrontendcommon";
import {
  CreateEmployeeResponse,
  EmployeeFormData,
  GetCompanyEmployeeListRequest,
  GetCompanyEmployeeListResponse,
  GetEmployeeDetailRequest,
  GetEmployeeDetailResponse,
  GetStoreEmployeeListRequest,
  GetStoreEmployeeListResponse,
  UpdateEmployeeRequest,
  DeleteEmployeeRequest,
  UpdateEmployeeResponse,
  DeleteEmployeeResponse
} from "../../components/Employees/types/employees";
import {
  createEmployeeQuery,
  getCompanyEmployeeListQuery,
  getEmployeeDetailQuery,
  getStoreEmployeeListQuery,
  updateEmployeeQuery,
  deleteEmployeeQuery
} from "../../Graphql/querries/employees";
import {
  setEmployeeDetail,
  setEmployeeList
} from "../../store/slices/employeeSlice";
import { API_RESPONSE_CODE } from "@kivunova/kivufrontendcommon";

import { LOG_LEVEL, buildLogContext, sendAppLog } from "../../Utils/appLogger";

/**
 * useGetStoreEmployeeList
 * Fetch store employee list
 */
export const useGetStoreEmployeeList = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();

  return useMutation<
    GetStoreEmployeeListResponse,
    Error,
    GetStoreEmployeeListRequest
  >({
    mutationKey: ["getStoreEmployeeList"],
    mutationFn: (input) => {
      sendAppLog(
        LOG_LEVEL.INFO,
        "Started fetching store employee list",
        buildLogContext(
          "employees",
          "get-store-employee-list",
          // userId not available — this is a list fetch, not user-scoped
          undefined,
          input.companyId,
          undefined
        )
      );
      return fetchGraphQL(getStoreEmployeeListQuery, { input }, setAlert);
    },
    onSuccess: (response) => {
      dispatch(setEmployeeList(response.listStoreUsers.users));
    }
  });
};

/**
 * useGetCompanyEmployeeList
 * Fetch company employee list
 */
export const useGetCompanyEmployeeList = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();

  return useMutation<
    GetCompanyEmployeeListResponse,
    Error,
    GetCompanyEmployeeListRequest
  >({
    mutationKey: ["getCompanyEmployeeList"],
    mutationFn: (input) => {
      sendAppLog(
        LOG_LEVEL.INFO,
        "Started fetching company employee list",
        buildLogContext(
          "employees",
          "get-company-employee-list",
          // userId not available — this is a list fetch, not user-scoped
          undefined,
          input.companyId,
          // storeId not relevant for a company-wide employee list
          undefined
        )
      );
      return fetchGraphQL(getCompanyEmployeeListQuery, { input }, setAlert);
    },
    onSuccess: (response) => {
      dispatch(setEmployeeList(response.listCompanyUsers.users));
    }
  });
};

/**
 * useCreateEmployee
 * Create a new employee
 */
export const useCreateEmployee = () => {
  const { setAlert } = useContext(AlertBannerContext);

  return useMutation<CreateEmployeeResponse, Error, EmployeeFormData>({
    mutationKey: ["createEmployee"],
    mutationFn: (input) => {
      sendAppLog(
        LOG_LEVEL.INFO,
        "Started creating employee",
        buildLogContext(
          "employees",
          "create-employee",
          // userId not yet assigned — the new employee's id is unknown before creation
          undefined,
          input.companyId,
          input.storeId,
          { role: String(input.role) }
        )
      );
      return fetchGraphQL(createEmployeeQuery, { input }, setAlert);
    },
    onSuccess: (response) => {
      const { code, user } = response.createUser;
      const { id, companyId, storeId } = user || {};

      if (code !== API_RESPONSE_CODE.OK || !user) {
        // ⚠️ Expected API/business failure
        sendAppLog(
          LOG_LEVEL.WARNING,
          "Create employee rejected by API",
          buildLogContext(
            "employees",
            "create-employee",
            // id is undefined when creation failed — user object may be absent
            id,
            companyId,
            storeId,
            { apiCode: String(code) }
          )
        );

        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: code as unknown as ALERT_BANNER_CODE
        });
      } else {
        // ✅ Success
        sendAppLog(
          LOG_LEVEL.INFO,
          "Employee created successfully",
          buildLogContext(
            "employees",
            "create-employee",
            id,
            companyId,
            storeId
          )
        );
      }
    }
  });
};

/**
 * useGetEmployeeDetail
 * Get employee details by id
 */
export const useGetEmployeeDetail = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();

  return useMutation<
    GetEmployeeDetailResponse,
    Error,
    GetEmployeeDetailRequest
  >({
    mutationKey: ["getEmployeeDetail"],
    mutationFn: (input) => {
      sendAppLog(
        LOG_LEVEL.INFO,
        "Started fetching employee detail",
        buildLogContext(
          "employees",
          "get-employee-detail",
          input.userId,
          // companyId not on GetEmployeeDetailRequest — employee is looked up by userId
          undefined,
          // storeId not on GetEmployeeDetailRequest
          undefined
        )
      );
      return fetchGraphQL(getEmployeeDetailQuery, { input }, setAlert);
    },
    onSuccess: (response) => {
      dispatch(setEmployeeDetail(response.getUser.user));
      return response.getUser.user;
    }
  });
};

/**
 * useUpdateEmployee
 * Update existing employee information
 */
export const useUpdateEmployee = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();

  return useMutation<UpdateEmployeeResponse, Error, UpdateEmployeeRequest>({
    mutationKey: ["updateEmployee"],
    mutationFn: (input) => {
      sendAppLog(
        LOG_LEVEL.INFO,
        "Started updating employee",
        buildLogContext(
          "employees",
          "update-employee",
          input.userId,
          // companyId not on UpdateEmployeeRequest
          undefined,
          // storeId not on UpdateEmployeeRequest
          undefined
        )
      );
      return fetchGraphQL(updateEmployeeQuery, { input }, setAlert);
    },
    onSuccess: (response) => {
      dispatch(setEmployeeDetail(response.updateUser.user));
    }
  });
};

/**
 * useDeleteEmployee
 * Delete existing employee
 */
export const useDeleteEmployee = () => {
  const { setAlert } = useContext(AlertBannerContext);

  return useMutation<DeleteEmployeeResponse, Error, DeleteEmployeeRequest>({
    mutationKey: ["deleteEmployee"],
    mutationFn: (input) => {
      sendAppLog(
        LOG_LEVEL.INFO,
        "Started deleting employee",
        buildLogContext(
          "employees",
          "delete-employee",
          input.userId,
          // companyId not on DeleteEmployeeRequest
          undefined,
          // storeId not on DeleteEmployeeRequest
          undefined
        )
      );
      return fetchGraphQL(deleteEmployeeQuery, { input }, setAlert);
    }
  });
};
